import * as api from '@owl-nest/api-client/latest'
import * as date from '@owl-nest/date'
import { t } from '@owl-nest/localize'
import * as logger from '@owl-nest/logger'
import { payment } from '@owl-nest/utils'
import * as env from '@owl-nest/config'
import * as plume from '@ulule/owl-kit-components/next'
import * as Enum from '@owl-nest/legacy-enum'

import * as i18n from '../i18n'
import * as image from '../image'
import * as subscriptionModel from '../subscription'

export const EXTRA_FIELDS = [
  'account',
  'analytics',
  'latest_project_online',
  'links',
  'partnerships',
  'rewards',
  'share_images',
  'shippings',
  'sponsorships',
  'tags',
  'socials',
] as const
export type ExtraFields = (typeof EXTRA_FIELDS)[number]
export type Project = api.Project<ExtraFields>

export type DonationFrequency = 'monthly' | 'one-time'

export const DonationAllowed = Enum.create({
  All: 'all',
  Monthly: 'monthly',
  OneTime: 'one-time',
})
export type DonationAllowed = Enum.ValueOf<typeof DonationAllowed>

export const OWNER_EXTRA_FIELDS = [
  'account',
  'analytics',
  'answer_code',
  'billing',
  'community_members',
  'default_manager',
  'examples',
  'fields_needed',
  'fieldsets',
  'latest_advices',
  'latest_news',
  'latest_project_online',
  'latest_supporter',
  'links',
  'manager',
  'membership.stats',
  'onboarding_steps',
  'owner.stats',
  'partnerships',
  'proposal',
  'published_mail',
  'rewards',
  'share_images',
  'shippings',
  'socials',
  'sponsorships',
  'sponsorships.channel',
  'stages',
  'tags',
  'tax_receipts_available',
  'thread',
  'unread_count',
  'user_orders',
  'user_role',
  'user_subscriptions',
] as const
export type ProjectOwnerExtraFields = (typeof OWNER_EXTRA_FIELDS)[number]
export type ProjectForOwner = api.Project<ProjectOwnerExtraFields>
export type UpdateProject = api.UpdateProject<ProjectOwnerExtraFields>

export const STAFF_EXTRA_FIELDS = [
  ...OWNER_EXTRA_FIELDS,
  'billing_blocked',
  'notes',
  'owner.contact_lang',
  'proposal.links',
  'quality_score',
  'recommendable',
  'slugs',
  'sensitive_terms',
] as const
export type StaffExtraFields = (typeof STAFF_EXTRA_FIELDS)[number]
export type ProjectForStaff = api.Project<StaffExtraFields>

export type BackofficeExtraFields = ProjectOwnerExtraFields | StaffExtraFields

export type RibbonData = {
  partnerOwnerId: number
  partnerUrl: string
  ribbonImageUrl: string | undefined
}

export const EditingUserRole = Enum.create({
  COORDINATOR: 'coordinator',
  EDITOR: 'editor',
  OWNER: 'owner',
  STAFF: 'staff',
})
export type EditingUserRole = Enum.ValueOf<typeof EditingUserRole>

export const LifecycleStatus = Enum.create({
  CANCELLED: 0,
  CLOSED: 1,
  EXTENDED: 2,
  FAILURE: 3,
  OFFLINE: 4,
  ONGOING: 5,
  REFUSED: 6,
  SUCCESS: 7,
})
export type LifecycleStatus = Enum.ValueOf<typeof LifecycleStatus>

const PRIMARY_PAYMENT_METHOD = 'card'

export function allowedDonationFrequency<EXTRA_FIELDS extends string = ''>(
  project: api.Project<EXTRA_FIELDS>,
): DonationAllowed {
  if (isUserMember(project)) {
    const subscription = getActiveMembershipSubscription(project)

    // Members with a paying subscription are not allowed to make a monthly donation
    // whereas members with a free subscription are
    if (
      (subscription && subscriptionModel.isMembershipPayingSubscription(subscription, project)) ||
      subscriptionModel.isMembershipRecurringDonation(subscription, project)
    ) {
      return DonationAllowed.OneTime
    }
  }

  // TODO: remove `isMembership` condition once `donations_monthly_allowed`is set to true by default on MB projects
  if (isMembership(project) || (project.donations_single_allowed && project.donations_monthly_allowed)) {
    return DonationAllowed.All
  }

  if (project.donations_monthly_allowed && !project.donations_single_allowed) {
    return DonationAllowed.Monthly
  }

  return DonationAllowed.OneTime
}

export function backgroundColor<EXTRA_FIELDS extends string = ''>(
  project: api.Project<EXTRA_FIELDS>,
): string | undefined {
  return project.background_color && i18n.get(project.background_color, project.lang as api.Lang)
}

export function backgroundImage<EXTRA_FIELDS extends string = ''>(
  project: api.Project<EXTRA_FIELDS>,
): string | undefined {
  const backgroundImageResource = project.background_image && i18n.get(project.background_image)
  return backgroundImageResource?.url
}

export function comingSoonLabel(project: api.Project, isStaff: boolean): string {
  if (
    isStaff ||
    (project.user_role &&
      (
        [api.UserRole.Coordinator, api.UserRole.Editor, api.UserRole.Moderator, api.UserRole.Owner] as api.UserRole[]
      ).includes(project.user_role))
  ) {
    return project.status === api.ProjectStatus.VALIDATED ? t('Ready to go online') : t('In progress')
  }
  return t('Online shortly, sign up now!')
}

export function currencySymbol<EXTRA_FIELDS extends string = ''>(project: api.Project<EXTRA_FIELDS>): string {
  const currency = project.currency
  const currencySymbol = env.CURRENCIES_SYMBOL[currency]
  return currencySymbol && currencySymbol.symbol
}

export function description<EXTRA_FIELDS extends string = ''>(project: api.Project<EXTRA_FIELDS>): string | undefined {
  return i18n.get(project.description || {}, language<EXTRA_FIELDS>(project) as api.Lang, '')
}

export function failure<EXTRA_FIELDS extends string = ''>(
  project: api.Project<EXTRA_FIELDS> | ProjectForOwner | ProjectForStaff,
): boolean {
  return isFinished(project) && !project.goal_raised
}

export function funded<EXTRA_FIELDS extends string = ''>(project: api.Project<EXTRA_FIELDS>): boolean {
  return !project.finished && progress(project) >= 100
}

export function fundingDescription<EXTRA_FIELDS extends string = ''>(
  project: api.Project<EXTRA_FIELDS>,
): string | undefined {
  return i18n.get(project.description_funding || {}, language(project) as api.Lang, '')
}

export function getCountDown<EXTRA_FIELDS extends string = ''>(
  project: api.Project<EXTRA_FIELDS>,
  short = true,
): string {
  if (!project.date_end) {
    return ''
  }
  const endDate = new Date(project.date_end)
  const countDown = date.getCountDown(endDate, short)

  if (project.type === api.ProjectType.Donation && countDown.value === '-') {
    return ''
  }

  return countDown.label
}

export function getEditingUserRole(project: api.Project, isStaff: boolean): EditingUserRole | undefined {
  if (isStaff) {
    return EditingUserRole.STAFF
  }
  if (project.user_role === api.UserRole.Owner) {
    return EditingUserRole.OWNER
  }
  if (project.user_role === api.UserRole.Editor) {
    return EditingUserRole.EDITOR
  }
  if (project.user_role === api.UserRole.Coordinator) {
    return EditingUserRole.COORDINATOR
  }

  return undefined
}

export function getFinishedLabel(endTime: string): string {
  const endDate = new Date(endTime)
  const currentDate = new Date()
  const hoursPassed = date.differenceInHours(currentDate, endDate)
  const minutesPassed = date.differenceInMinutes(currentDate, endDate)

  if (hoursPassed === 0) {
    return t('Just ended %(hours)d min ago!', { hours: minutesPassed })
  }

  if (hoursPassed < 12 && hoursPassed > 0) {
    return t('Just ended %(hours)dh ago!', { hours: hoursPassed })
  }

  return t('Finished')
}

export function getPaymentMethods<EXTRA_FIELDS extends string = ''>(
  project: api.Project<EXTRA_FIELDS>,
  enableApplePay?: boolean,
): payment.PaymentMethod[] {
  const paymentMethods = payment.getPaymentMethods(isDonationBased(project), enableApplePay)
  return project.payment_methods?.map((method) => paymentMethods[method]).filter((method) => method !== undefined) || []
}

export function getNumberOfPublicRewards(project: api.Project): number {
  return project.rewards?.filter((reward) => !reward.is_hidden && !reward.is_archived).length ?? 0
}

export function getOrderedPaymentMethods(
  project: api.Project,
  enablePaypal = true,
  enableApplePay = false,
): payment.PaymentMethod[] {
  const paymentMethods = getPaymentMethods(project, enableApplePay)

  // Primary method must always be first
  function sortPaymentMethods(methodA: payment.PaymentMethod, methodB: payment.PaymentMethod): number {
    return methodA.id === PRIMARY_PAYMENT_METHOD ? -1 : methodB.id === PRIMARY_PAYMENT_METHOD ? 1 : 0
  }

  return paymentMethods.filter((method) => enablePaypal || method.id !== 'paypal').sort(sortPaymentMethods)
}

export function getPrimaryPaymentMethod<EXTRA_FIELDS extends string = ''>(
  project: api.Project<EXTRA_FIELDS>,
  enablePaypal = true,
  enableApplePay = false,
): payment.PaymentMethod {
  const primaryPaymentMethod = getOrderedPaymentMethods(project, enablePaypal, enableApplePay)[0]

  if (!primaryPaymentMethod) {
    logger.warn('[project] Found no primary payment method', { project })
  }

  return primaryPaymentMethod || payment.getPaymentMethods()[PRIMARY_PAYMENT_METHOD]
}

export type StatusProperties = 'failed' | 'pending' | 'succeeded' | 'waiting'
export type StepProperties = {
  status: StatusProperties
  percent: number
}

export type Properties =
  | {
      preparation: StepProperties
      publishing: StepProperties
      reread: StepProperties
    }
  | undefined

export function getStepProperties(project: api.Project): Properties {
  const allRequiredFieldFilled = isAllFieldsetsFilled(project)

  switch (project.status) {
    case api.ProjectStatus.NEW:
      if (!allRequiredFieldFilled) {
        return {
          preparation: { status: 'pending', percent: 25 },
          reread: { status: 'waiting', percent: 0 },
          publishing: { status: 'waiting', percent: 0 },
        }
      } else {
        return {
          preparation: { status: 'pending', percent: 75 },
          reread: { status: 'waiting', percent: 0 },
          publishing: { status: 'waiting', percent: 0 },
        }
      }

    case api.ProjectStatus.PENDING:
      return {
        preparation: { status: 'succeeded', percent: 100 },
        reread: { status: 'pending', percent: 25 },
        publishing: { status: 'waiting', percent: 0 },
      }
    case api.ProjectStatus.PENDING_OWNER:
    case api.ProjectStatus.PENDING_FINAL_VALIDATION:
      if (project.moderation_message) {
        if (project.moderation_message.read_by_owner) {
          return {
            preparation: { status: 'succeeded', percent: 100 },
            reread: { status: 'pending', percent: 75 },
            publishing: { status: 'waiting', percent: 0 },
          }
        }
        return {
          preparation: { status: 'succeeded', percent: 100 },
          reread: { status: 'pending', percent: 50 },
          publishing: { status: 'waiting', percent: 0 },
        }
      }

      return {
        preparation: { status: 'succeeded', percent: 100 },
        reread: { status: 'pending', percent: 75 },
        publishing: { status: 'waiting', percent: 0 },
      }

    case api.ProjectStatus.VALIDATED:
      if (project.validation_message === undefined) {
        return {
          preparation: { status: 'succeeded', percent: 100 },
          reread: { status: 'succeeded', percent: 100 },
          publishing: { status: 'pending', percent: 75 },
        }
      } else if (project.validation_message.read_by_owner) {
        return {
          preparation: { status: 'succeeded', percent: 100 },
          reread: { status: 'succeeded', percent: 100 },
          publishing: { status: 'pending', percent: 75 },
        }
      }

      return {
        preparation: { status: 'succeeded', percent: 100 },
        reread: { status: 'succeeded', percent: 100 },
        publishing: { status: 'pending', percent: 25 },
      }

    default:
      return
  }
}

export const Steps = Enum.create({
  SETUP: 'setup',
  AWAITING_REVIEW_SUBMISSION: 'awaiting_review_submission',
  IN_REVIEW: 'in_review',
  AWAITING_OWNER_REVIEW_REPLY: 'awaiting_owner_review_reply',
  AWAITING_VALIDATION: 'awaiting_validation',
  AWAITING_OWNER_VALIDATION_ACKNOWLEDGMENT: 'awaiting_owner_validation_acknowledgement',
  AWAITING_OWNER_PUBLISHING: 'awaiting_owner_publishing',
})
export type Steps = Enum.ValueOf<typeof Steps>

export function getCurrentStep(project: api.Project) {
  const properties = getStepProperties(project)

  if (!properties) {
    return
  }

  if (properties.preparation.status === 'pending') {
    if (properties.preparation.percent > 50) {
      return Steps.AWAITING_REVIEW_SUBMISSION
    }

    if (properties.preparation.percent < 50) {
      return Steps.SETUP
    }
  }

  if (properties.reread.status === 'pending') {
    if (properties.reread.percent === 25) {
      return Steps.IN_REVIEW
    }

    if (properties.reread.percent === 50) {
      return Steps.AWAITING_OWNER_REVIEW_REPLY
    }

    if (properties.reread.percent === 75) {
      return Steps.AWAITING_VALIDATION
    }
  }

  if (properties.publishing.status === 'pending') {
    if (properties.publishing.percent === 25) {
      return Steps.AWAITING_OWNER_VALIDATION_ACKNOWLEDGMENT
    }

    if (properties.publishing.percent === 75) {
      return Steps.AWAITING_OWNER_PUBLISHING
    }
  }
}

export function getFieldset(project: api.Project, key: api.FIELDSETS_KEYS) {
  return project.fieldsets?.find((fieldset) => fieldset.key === key)
}

export function getRibbonData(project: api.Project): RibbonData | null {
  if (!project || !project.partnerships) {
    return null
  }

  const ribbonPartnership = project.partnerships.find((partnership) => !!partnership.ribbon)

  if (ribbonPartnership) {
    return {
      partnerOwnerId: ribbonPartnership.partner.user_id,
      partnerUrl: ribbonPartnership.partner.url,
      ribbonImageUrl: ribbonPartnership.ribbon?.url,
    }
  }

  const winnerSponsor = project.partnerships.find(
    (partnership) => partnership.is_winner && partnership.partner.ribbon_winner,
  )

  if (winnerSponsor) {
    return {
      partnerOwnerId: winnerSponsor.partner.user_id,
      partnerUrl: winnerSponsor.partner.url,
      ribbonImageUrl: winnerSponsor.partner.ribbon_winner?.url,
    }
  }

  const supportiveSponsor = project.partnerships.find(
    (partnership) => partnership.is_support && partnership.partner.ribbon,
  )

  if (supportiveSponsor) {
    return {
      partnerOwnerId: supportiveSponsor.partner.user_id,
      partnerUrl: supportiveSponsor.partner.url,
      ribbonImageUrl: supportiveSponsor.partner.ribbon?.url,
    }
  }

  return null
}

export function getSecondaryPaymentMethods<EXTRA_FIELDS extends string = ''>(
  project: api.Project<EXTRA_FIELDS>,
  enablePaypal = true,
  enableApplePay = false,
): payment.PaymentMethod[] {
  const paymentMethods = payment.getPaymentMethods(isDonationBased(project), enableApplePay)
  const primaryPaymentMethod = getPrimaryPaymentMethod(project, enablePaypal, enableApplePay)
  return (
    (project.payment_methods ?? [])
      .map((method) => paymentMethods[method])
      .filter((method) => method !== undefined && method.id !== primaryPaymentMethod?.id) || []
  )
}

export function getStatus(project: api.Project): string | undefined {
  if (project.is_in_extra_time) {
    return t('Extension')
  } else if (project.finished) {
    return t('Finished')
  } else if (project.is_online && !project.date_end) {
    return ''
  }
}

export function hadExtraTime<EXTRA_FIELDS extends string = ''>(project: api.Project<EXTRA_FIELDS>): boolean {
  if (!project.date_end_extra_time) {
    return false
  }

  const dateEndExtraTime = project.extra_time_canceled_at
    ? new Date(project.extra_time_canceled_at)
    : new Date(project.date_end_extra_time)
  const currentDate = new Date()

  return isFinished(project) && date.compareAsc(currentDate, dateEndExtraTime) >= 0
}

const FIELDSET_NEEDED_FOR_PREVIEW = [
  api.FIELDSETS_KEYS.DATE_END,
  api.FIELDSETS_KEYS.GOAL,
  api.FIELDSETS_KEYS.MAIN_IMAGE,
  api.FIELDSETS_KEYS.NB_DAYS,
  api.FIELDSETS_KEYS.TITLE,
] as api.FIELDSETS_KEYS[]

type HasAccessToPreviewAllowKey = (typeof FIELDSET_NEEDED_FOR_PREVIEW)[number]

export function hasAccessToPreview(project: api.Project): boolean {
  const projectIsDonationBased = isDonationBased(project)
  const projectIsRewardBased = isRewardBased(project)
  const fieldsNeeded = project.fieldsets
  const KEYS = api.FIELDSETS_KEYS

  if (isAllFieldsetsFilled(project)) {
    return true
  }

  const fieldsFiltered = fieldsNeeded?.reduce(
    (fieldAcc, field) => {
      if (FIELDSET_NEEDED_FOR_PREVIEW.includes(field.key)) {
        return { ...fieldAcc, [field.key]: { ...field } }
      }

      return fieldAcc
    },
    [] as { [key in HasAccessToPreviewAllowKey]?: api.ProjectFieldset },
  )

  if (projectIsDonationBased) {
    return (
      (fieldsFiltered !== undefined &&
        fieldsFiltered[KEYS.TITLE]?.completed &&
        fieldsFiltered[KEYS.MAIN_IMAGE]?.completed) ??
      false
    )
  } else if (projectIsRewardBased) {
    //NOTE : we remove the DATE condition since it can't be undefined when goal is set
    // (they are set on the same settings screen)
    return (
      (fieldsFiltered !== undefined &&
        getNumberOfPublicRewards(project) > 0 &&
        fieldsFiltered[KEYS.TITLE]?.completed &&
        fieldsFiltered[KEYS.MAIN_IMAGE]?.completed &&
        fieldsFiltered[KEYS.GOAL]?.completed) ??
      false
    )
  } else {
    // Membership projects
    return (
      (fieldsFiltered !== undefined &&
        getNumberOfPublicRewards(project) > 0 &&
        fieldsFiltered[KEYS.TITLE]?.completed &&
        fieldsFiltered[KEYS.MAIN_IMAGE]?.completed) ??
      false
    )
  }
}

export function hasAccessToPrelaunch(project: api.Project): boolean {
  return hasAccessToPreview(project) && isComingSoonEnabled(project)
}

// return if project has a checkout
export function hasCheckout(project: api.Project): boolean {
  if (project.user_role === api.UserRole.Owner) {
    return true
  }

  if (isFinished(project)) {
    return project.post_campaign_link !== undefined
  }

  return project.is_online && !isCancelled(project)
}

export function hasExtraTime<EXTRA_FIELDS extends string = ''>(project: api.Project<EXTRA_FIELDS>): boolean {
  return project.has_extra_time
}

export function getStatsEffectivePeriodEnd<EXTRA_FIELDS extends string = ''>(project: api.Project<EXTRA_FIELDS>): Date {
  const projectEndDate = hadExtraTime(project) || hasExtraTime(project) ? project.date_end_extra_time : project.date_end
  const periodEnd = projectEndDate ? date.createDateWithTimezone(projectEndDate, 'UTC') : new Date()
  const today = new Date()
  return periodEnd > today ? today : periodEnd
}

export function hasSubscriptions<EXTRA_FIELDS extends string = ''>(project: api.Project<EXTRA_FIELDS>): boolean {
  return project.subscriptions_count !== undefined && project.subscriptions_count >= 1
}

export function hasSelfPermission<EXTRA_FIELDS extends string = ''>(
  project: api.Project<EXTRA_FIELDS>,
  permission: api.ProjectSelfPermission,
): boolean {
  if (project.permissions.self === null) {
    return false
  }
  return project.permissions.self.includes(permission)
}

export function isAllFieldsetsFilled(project: api.Project) {
  const fieldsets = project.fieldsets
  return fieldsets !== undefined && fieldsets.every((field) => !field.required || field.completed)
}

export function isCancelled<EXTRA_FIELDS extends string = ''>(
  project: api.Project<EXTRA_FIELDS> | ProjectForOwner | ProjectForStaff,
): boolean {
  return project.goal_status === api.GoalStatus.CANCELLED || project.is_cancelled
}

export function isFraud<EXTRA_FIELDS extends string = ''>(
  project: api.Project<EXTRA_FIELDS> | ProjectForOwner | ProjectForStaff,
): boolean {
  return project.goal_status === api.GoalStatus.FRAUD
}

export function isClosed(project: api.Project): boolean {
  return isFinished(project) || isCancelled(project)
}

export function isDeleted<EXTRA_FIELDS extends string = ''>(
  project: api.Project<EXTRA_FIELDS> | ProjectForOwner | ProjectForStaff | undefined,
): boolean {
  return Boolean(project && project.goal_status === api.GoalStatus.DELETED)
}

// Although we avoid implementing such simple model methods, this one is here for a purpose:
// be able to easily locate view sections and logic blocks that are related to donation-based projects.
export function isDonationBased(project: api.Project): boolean {
  return project.type === api.ProjectType.Donation
}

export function isMembership(project: api.Project): boolean {
  return project.type === api.ProjectType.Membership
}

export function isRewardBased(project: api.Project): boolean {
  return project.type === api.ProjectType.Presale || project.type == api.ProjectType.Project
}

export function isVendorBased(project: api.Project): boolean {
  return project.type === api.ProjectType.Vendor
}

export function isExtraTime<EXTRA_FIELDS extends string = ''>(
  project: api.Project<EXTRA_FIELDS> | ProjectForOwner | ProjectForStaff,
): boolean {
  return project.is_in_extra_time
}

export function extraTimeStartDate<EXTRA_FIELDS extends string = ''>(
  project: api.Project<EXTRA_FIELDS> | ProjectForOwner | ProjectForStaff,
): Date | undefined {
  if (!project.has_extra_time || !project.date_end) {
    return undefined
  }
  return date.addDays(new Date(project.date_end), 1)
}

export function isFinished<EXTRA_FIELDS extends string = ''>(
  project: api.Project<EXTRA_FIELDS> | ProjectForOwner | ProjectForStaff,
): boolean {
  return project.finished && !project.is_in_extra_time
}

export function isComingSoonEnabled(project: api.Project): boolean {
  return (
    !project.is_cancelled &&
    !([api.ProjectStatus.ONLINE, api.ProjectStatus.REFUSED] as api.ProjectStatus[]).includes(project.status) &&
    project.answer_code !== api.ProjectAnswerCode.NEED_MODERATION
  )
}

export function isOnline<EXTRA_FIELDS extends string = ''>(
  project: api.Project<EXTRA_FIELDS> | ProjectForOwner | ProjectForStaff,
): boolean {
  return project.status === api.ProjectStatus.ONLINE
}

export function isNew<EXTRA_FIELDS extends string = ''>(
  project: api.Project<EXTRA_FIELDS> | ProjectForOwner | ProjectForStaff,
): boolean {
  return project.status === api.ProjectStatus.NEW
}

export function isOkpalOffboardingAvailable<EXTRA_FIELDS extends string = ''>(
  project: api.Project<EXTRA_FIELDS>,
): boolean {
  return project.currency === 'EUR' || project.currency === 'CAD'
}

export function isValidated<EXTRA_FIELDS extends string = ''>(
  project: api.Project<EXTRA_FIELDS> | ProjectForOwner | ProjectForStaff,
): boolean {
  return project.status === api.ProjectStatus.VALIDATED
}

export function language<EXTRA_FIELDS extends string = ''>(project: api.Project<EXTRA_FIELDS>): string | undefined {
  return project?.lang
}

export function lifecycleStatus<EXTRA_FIELDS extends string = ''>(project: api.Project<EXTRA_FIELDS>): LifecycleStatus {
  // Project with status 'refused' or goal_status 'fraud'
  if (refused(project) || project.goal_status === api.GoalStatus.FRAUD) {
    return LifecycleStatus.REFUSED
  }

  // Project with goal_status 'cancelled', no matters the status
  if (isCancelled(project)) {
    return LifecycleStatus.CANCELLED
  }

  // Project not online yet (status 'new', 'pending', 'pending-owner', 'waiting',
  // 'pending-final-validation' or 'validated'
  if (offline(project)) {
    return LifecycleStatus.OFFLINE
  }

  // At this point, all statuses are handled except 'online'

  // RB finished and successful projects or DB/MB closed projects
  if (success(project)) {
    if (isRewardBased(project)) {
      return LifecycleStatus.SUCCESS
    }

    return LifecycleStatus.CLOSED
  }

  // RB successful and extended projects
  if (!isDonationBased(project) && project.is_in_extra_time) {
    return LifecycleStatus.EXTENDED
  }

  // Failed projects
  if (failure(project)) {
    return LifecycleStatus.FAILURE
  }

  // Others projects including:
  // - RB projects with status 'online' and goal_status 'funding'
  // - DB/MB projects that are not finished, with status 'online'
  // and goal_status 'failed' or 'funding'
  return LifecycleStatus.ONGOING
}

export function mainImage<EXTRA_FIELDS extends string = ''>(
  project: api.Project<EXTRA_FIELDS>,
  size = 'large',
  lang = env.getUserConfig().lang,
): string | undefined {
  const mainImageResource = project.main_image && i18n.getWithLang(project.main_image, lang, project.lang)
  return image.getUrl(mainImageResource?.versions, size)
}

export function shareImages(project: api.Project, lang = env.getUserConfig().lang): string | undefined {
  return project.share_images?.facebook && i18n.getWithLang(project.share_images.facebook, lang, project.lang)
}

export function avatarImage(project: api.Project, lang = env.getUserConfig().lang, size: string): string | undefined {
  const avatarImageResource = project.avatar_image && i18n.getWithLang(project.avatar_image, lang, project.lang)
  return image.getUrl(avatarImageResource?.versions, size)
}

export function mainTag<EXTRA_FIELDS extends string = ''>(project: api.Project<EXTRA_FIELDS>): string | undefined {
  return project.main_tag && i18n.get(project.main_tag.name, project.lang as api.Lang)
}

export function name<EXTRA_FIELDS extends string = ''>(
  project: api.Project<EXTRA_FIELDS>,
  overrideLang?: api.Lang,
  fallbackLang?: api.Lang,
): string | undefined {
  if (overrideLang) {
    return (
      project.name[overrideLang] ??
      /* HACK: Cannot solely rely on i18n.get() on ulule/next since it depends on "ufe.locale", which doesn't reflect the real-world value. */
      // HACK: i18n.get uses UFE.USER_LOCALE first, which happen to be "fr" on @ulule/next,
      // but current production values doesn't reflect, that so we're controlling it from here.
      // If the targeted project.name[overrideLang] fails we try project.name[fallbackLang]
      // if fallbackLang is defined
      (fallbackLang && project.name[fallbackLang]) ??
      // and eventually fallback to UFE.USER_LOCALE from within i18n.get
      i18n.get(project.name, project.lang as api.Lang)
    )
  }

  return i18n.get(project.name, project.lang as api.Lang)
}

export function ownerDescription<EXTRA_FIELDS extends string = ''>(
  project: api.Project<EXTRA_FIELDS>,
  lang = env.getUserConfig().lang,
): string | undefined {
  return i18n.getWithLang(project.description_yourself || {}, lang, project.lang)
}

export function progress<EXTRA_FIELDS extends string = ''>(project: api.Project<EXTRA_FIELDS>): number {
  return Math.floor(rawProgress(project))
}

export function rawProgress<EXTRA_FIELDS extends string = ''>(project: api.Project<EXTRA_FIELDS>): number {
  const projectGoal = project.goal

  if (projectGoal <= 0) {
    return 0
  }

  let value = project.amount_raised
  if (project.type === api.ProjectType.Presale) {
    value = project.nb_products_sold
  } else if (project.type === api.ProjectType.Membership) {
    if (project.goal_type === api.GoalType.QUANTITATIVE && project.subscriptions_count) {
      value = project.subscriptions_count
    } else if (project.goal_type === api.GoalType.FINANCIAL && project.subscriptions_amount) {
      value = project.subscriptions_amount
    } else {
      value = 0
    }
  }

  return (value * 100) / projectGoal
}

export function refused<EXTRA_FIELDS extends string = ''>(project: api.Project<EXTRA_FIELDS>): boolean {
  return project.status === api.ProjectStatus.REFUSED
}

export function success<EXTRA_FIELDS extends string = ''>(project: api.Project<EXTRA_FIELDS>): boolean {
  return project.goal_status === api.GoalStatus.SUCCESS && !project.is_in_extra_time
}

export function offline<EXTRA_FIELDS extends string = ''>(project: api.Project<EXTRA_FIELDS>): boolean {
  return (
    project.status === api.ProjectStatus.PENDING ||
    project.status === api.ProjectStatus.PENDING_FINAL_VALIDATION ||
    project.status === api.ProjectStatus.PENDING_OWNER ||
    project.status === api.ProjectStatus.VALIDATED ||
    project.status === api.ProjectStatus.WAITING ||
    project.status === api.ProjectStatus.NEW
  )
}

/**
 * The manager type a send has to move the project to on its own, or undefined
 * when it has nothing to say and the sender's own choice stands.
 *
 * Sending on a low-scored project puts it back on the specialist queue: those
 * scores are the ones the coaching pipe expects a specialist to pick up, and
 * leaving them on `success` hides them from that queue. The rule wins over a
 * pipe picked in the validation modal — a coach validating a C project into
 * "en cours de réponse" is the exact case it exists to catch.
 */
export function resolveMessageManagerType(
  project: Pick<api.Project, 'manager_type'>,
  qualityScore: api.QualityScore | undefined,
): api.ProjectManagerType | undefined {
  if (qualityScore !== undefined && ['C', 'X', 'Y'].includes(qualityScore) && project.manager_type === 'success') {
    return 'specialist'
  }

  return undefined
}

export function subtitle<EXTRA_FIELDS extends string = ''>(
  project: api.Project<EXTRA_FIELDS>,
  lang = env.getUserConfig().lang,
): string | undefined {
  return i18n.getWithLang(project.subtitle, lang, project.lang)
}

export function showOnboardingSteps(project: api.Project): boolean {
  if (project.status !== api.ProjectStatus.NEW) {
    return false
  }

  // when using old onboarding, we rely on step 'slug'
  const onboardingSteps = project.onboarding_steps

  // when using new onboarding, we have to rely on step 'done' if we want to avoid confettis everytime
  if (onboardingSteps?.onboarding?.done) {
    return false
  }

  return project.user_role === api.UserRole.Owner
}

export function tagName<EXTRA_FIELDS extends string = ''>(
  tag: api.TagBase,
  project: api.Project<EXTRA_FIELDS>,
): string | undefined {
  return i18n.get(tag.name, project.lang as api.Lang)
}

export function video<EXTRA_FIELDS extends string = ''>(
  project: api.Project<EXTRA_FIELDS>,
): api.VideoResource | undefined {
  return project.video && i18n.get(project.video, project.lang as api.Lang)
}

export function isOrdersEnabled<EXTRA_FIELDS extends string = ''>(project: api.Project<EXTRA_FIELDS>): boolean {
  return project.orders_enabled !== undefined ? project.orders_enabled : true
}

export function isPresaleType(project: api.Project): boolean {
  return project.type === api.ProjectType.Presale
}

export function getShippingFormattedDate(project: api.Project, format = 'MMMM yyyy') {
  if (project.date_delivery === undefined) {
    return ''
  }

  return date.format(date.parseISO(project.date_delivery), format)
}

export function isUserMember(project: api.Project): boolean {
  return getActiveMembershipSubscription(project) !== undefined
}

export function getActiveMembershipSubscription(project: api.Project): api.Subscription | undefined {
  return project.user_subscriptions?.[0]
}

export function getAutomaticGoalValue(project: api.Project): number | undefined {
  const currentAmount = getSubscriptionsCount(project)

  if (currentAmount === undefined) {
    return
  }

  const levels =
    project.goal_type === api.GoalType.QUANTITATIVE
      ? [
          { target: 10, step: 10 },
          { target: 100, step: 25 },
          { target: 500, step: 50 },
          { target: 1000, step: 100 },
          { target: 5000, step: 250 },
          { target: 10000, step: 500 },
          { target: 50000, step: 2500 },
          { target: Infinity, step: 5000 },
        ]
      : [
          { target: 100, step: 25 },
          { target: 500, step: 50 },
          { target: 1000, step: 100 },
          { target: 5000, step: 250 },
          { target: 10000, step: 500 },
          { target: 50000, step: 2500 },
          { target: Infinity, step: 5000 },
        ]

  let nextGoal = 1
  let i = 0

  while (nextGoal === 1) {
    const level = levels[i]
    if (currentAmount < level.target) {
      nextGoal = (Math.floor(currentAmount / level.step) + 1) * level.step
    } else {
      i += 1
    }
  }

  return nextGoal
}

export function getSubscriptionLabel(project: api.Project, lang: api.Lang): string | undefined {
  const subscription = getActiveMembershipSubscription(project)
  const reward = subscription?.reward
  const subscriptionName = i18n.getWithLang(reward?.title ?? {}, lang, project.lang)

  if (subscriptionModel.isMembershipRecurringDonation(subscription, project)) {
    return t('You are a member with a monthly donation of %(total)s', {
      total: plume.accounting.localeMoney(subscription?.total, {
        currency: project.currency,
        lang: lang,
      }),
    })
  }

  if (reward?.is_free) {
    return t('You are a free member')
  }

  if (subscriptionName) {
    return t('You are a %(subscriptionName)s member', {
      subscriptionName: subscriptionName,
    })
  }

  return undefined
}

export function getSubscriptionType(project: api.Project) {
  const subscription = getActiveMembershipSubscription(project)
  const reward = subscription?.reward

  if (subscriptionModel.isMembershipRecurringDonation(subscription, project)) {
    return 'recurring-donation'
  }

  if (reward) {
    return reward.is_free ? 'free' : 'paid'
  }
  return 'none'
}

// FIXME: We need to rename this.
export function getSubscriptionsCount(project: api.Project) {
  return project.goal_type === api.GoalType.QUANTITATIVE ? project.subscriptions_count : project.subscriptions_amount
}
